import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { LuBuilding, LuChartColumn, LuDownload, LuLoaderCircle, LuMap, LuRefreshCw, LuTriangleAlert, LuUsers } from 'react-icons/lu';
import { collection, getDocs, doc, query, where, getDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { useUserProfile } from '../contexts/UserProfileContext';
import useTestDataFilters from '../hooks/useTestDataFilters';
import PageHero from '../components/data/PageHero';
import Button from '../components/ui/Button';
import GlassIcon from '../components/ui/GlassIcon';
import ProjectVisitsModal from './ProfilePage/ProjectVisitsModal';
import { CARD, EmptyState } from './ProfilePage/components/ProfileUi';
import { normalizeCity, normalizeManagerIds } from './CenterManagementPage/regionUtils';
import {
  isArchivedAssignment,
  formatUnassignedDate,
} from '../utils/assignmentHistory';
import { matchesSearchTerm } from '../utils/searchText';
import { fetchCityBasedEmployees, foldCity, getCenterAssignedIds } from '../utils/regionScope';
import { getTestProjectIds } from '../utils/testData';
import useSmoText from './SMOLeadReports/smoText';
import { Stat } from './SMOLeadReports/parts/SmoHeader';
import CityCenterGroups from './SMOLeadReports/parts/CityCenterGroups';
import WorkerFilters from './SMOLeadReports/parts/WorkerFilters';
import WorkersTable from './SMOLeadReports/parts/WorkersTable';
import ReportSection, { DateRangeBar, SectionStatus } from './SMOLeadReports/parts/ReportSection';
import useRegionText from './BolgeMudurleriReports/regionText';
import RegionPicker from './BolgeMudurleriReports/RegionPicker';
import RegionSummary from './BolgeMudurleriReports/RegionSummary';
import ProjectlessWorkers from './BolgeMudurleriReports/ProjectlessWorkers';

/**
 * Bölge Müdürleri Raporları: a region manager's region (or a single city of
 * it) with its centres, the workers assigned there and an operational
 * summary of their visits. It shares the worker table, filters and report
 * section with SMO Lead Raporları (./SMOLeadReports/parts); the data logic
 * below is unchanged.
 */
const BolgeMudurleriReports = () => {
  const { employeeId, userAuthorization } = useUserProfile();
  const { label, language } = useRegionText();
  const { label: smo } = useSmoText();
  // Toasts inside effects / callbacks read the current language through a ref,
  // so switching the language doesn't refetch anything
  const labelRef = useRef(label);
  labelRef.current = label;

  // Test kullanıcı, proje ve merkezleri yalnızca ana admine görünür. Burada
  // ayıklanan kayıtlar sayaçlara, özetlere ve Excel'e de yansımaz.
  const testFilters = useTestDataFilters();
  const testProjectIdsRef = useRef(new Set());

  // Region manager list state — one entry per region that has a manager
  const [managerViews, setManagerViews] = useState([]);
  const [selectedView, setSelectedView] = useState(null);
  const [loadingManagers, setLoadingManagers] = useState(true);

  // Workers under selected region
  const [workers, setWorkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  // Workers with no centre assignment, shown here through their Çalıştığı İl
  const [projectlessWorkers, setProjectlessWorkers] = useState([]);

  // Search & filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [primerFilter, setPrimerFilter] = useState('all');
  const [blindedFilter, setBlindedFilter] = useState('all');
  const [hideFinished, setHideFinished] = useState(false);

  // Table scroll state
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const tableContainerRef = useRef(null);

  // Visits modal state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showVisitsModal, setShowVisitsModal] = useState(false);
  const [expandedWorkers, setExpandedWorkers] = useState({});

  // Visits for the selected region's workers
  const [allVisits, setAllVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);

  // Operational summary section state
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  
  // Collapsed cities state - tracks which cities are collapsed for each view
  const [collapsedCities, setCollapsedCities] = useState({});

  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin');

  // ── Date range filter for the summary section ──────────────────────────
  const getMonthStart = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  };
  const getToday = () => new Date().toISOString().split('T')[0];

  const [dateStart, setDateStart] = useState(getMonthStart);
  const [dateEnd, setDateEnd] = useState(getToday);

  const rangedVisits = useMemo(() => {
    return (allVisits || []).filter(v => {
      const d = v.date || v.firstVisitDate || '';
      if (!d) return false;
      if (dateStart && d < dateStart) return false;
      if (dateEnd && d > dateEnd) return false;
      return true;
    });
  }, [allVisits, dateStart, dateEnd]);

  // ── Operational summary (same metrics as SMO Lead reports) ─────────────
  const summary = useMemo(() => {
    const visits = rangedVisits;
    if (!visits || visits.length === 0) return null;

    const total = visits.length;
    const homeVisits = visits.filter(v => v.visitType === 'home');
    const siteVisits = visits.filter(v => v.visitType !== 'home');
    const pct = (n, d) => d > 0 ? Math.round(n / d * 100) : 0;

    const issueCount = visits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length;
    const qualityRate = pct(total - issueCount, total);

    const withSLA = visits.filter(v =>
      v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate
    );
    const slaMet = withSLA.filter(v => {
      const cycle = Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000);
      return cycle <= parseFloat(v.visitEvaluation.slaTarget);
    }).length;
    const slaRate = pct(slaMet, withSLA.length);
    const overdueCount = withSLA.length - slaMet;

    const ecrfDone = visits.filter(v => v.visitEvaluation?.ecrfCompletionDate).length;
    const sourceDone = visits.filter(v => v.visitEvaluation?.sourceDocCompletionDate).length;
    const signDone = visits.filter(v => v.visitEvaluation?.signatureCompletionDate).length;
    const docRate = Math.round((pct(ecrfDone, total) + pct(sourceDone, total) + pct(signDone, total)) / 3);

    const missedCount = visits.filter(v => v.visitEvaluation?.issueResultedInMiss === 'yes').length;
    const deviationCount = visits.filter(v => v.visitEvaluation?.protocolDeviation === 'yes').length;
    const deviationRate = pct(deviationCount, total);
    const aeSaeCount = visits.filter(v => v.visitEvaluation?.wasAeSaeTaken === 'yes').length;
    const missingProcessEnd = visits.filter(v => !v.visitEvaluation?.processEndDate).length;

    const totalKm = Math.round(visits.reduce((sum, v) =>
      sum + (parseFloat(v.visitEvaluation?.travelDistanceBefore) || 0), 0));

    const alerts = [];
    if (withSLA.length > 0 && slaRate < 80) alerts.push({ type: 'danger', metric: 'overdue', text: `SLA karşılanma oranı %${slaRate} — hedefin altında`, vars: { rate: slaRate } });
    if (pct(issueCount, total) > 10) alerts.push({ type: 'danger', metric: 'issues', text: `Sorun oranı %${pct(issueCount, total)} — yüksek seviyede`, vars: { rate: pct(issueCount, total) } });
    if (deviationRate > 5) alerts.push({ type: 'warning', metric: 'deviation', text: `Protokol sapma oranı %${deviationRate}`, vars: { rate: deviationRate } });
    if (missingProcessEnd > total * 0.2) alerts.push({ type: 'warning', metric: 'missingProcessEnd', text: `${missingProcessEnd} vizitte süreç bitiş tarihi eksik`, vars: { count: missingProcessEnd } });
    if (missedCount > 0) alerts.push({ type: 'danger', metric: 'missed', text: `${missedCount} vizit kaçırıldı`, vars: { count: missedCount } });
    if (aeSaeCount > 0) alerts.push({ type: 'info', metric: 'aesae', text: `${aeSaeCount} AE/SAE rapor edildi`, vars: { count: aeSaeCount } });

    // Monthly trend
    const monthlyData = {};
    visits.forEach(v => {
      const date = v.date || v.firstVisitDate || '';
      const month = date ? date.substring(0, 7) : null;
      if (!month) return;
      if (!monthlyData[month]) monthlyData[month] = { month, Ev: 0, Hastane: 0, Sorun: 0 };
      if (v.visitType === 'home') monthlyData[month].Ev++;
      else monthlyData[month].Hastane++;
      if (v.visitEvaluation?.issueOccurred === 'yes') monthlyData[month].Sorun++;
    });
    const monthlyTrend = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));

    // Per-worker visit distribution chart
    const perWorker = {};
    visits.forEach(v => {
      const w = workers.find(wk => wk.id === v.employeeId);
      const name = w ? `${w.name} ${w.surname}` : (v.employeeName || 'Bilinmeyen');
      if (!perWorker[name]) perWorker[name] = { name, Vizit: 0, Sorun: 0 };
      perWorker[name].Vizit++;
      if (v.visitEvaluation?.issueOccurred === 'yes') perWorker[name].Sorun++;
    });
    const workerChart = Object.values(perWorker)
      .sort((a, b) => b.Vizit - a.Vizit)
      .slice(0, 12)
      .map(w => ({ ...w, name: w.name.length > 16 ? w.name.substring(0, 15) + '…' : w.name, fullName: w.name }));

    return {
      total, homeCount: homeVisits.length, siteCount: siteVisits.length,
      qualityRate, slaRate, docRate, totalKm,
      issueCount, missedCount, deviationCount, aeSaeCount,
      overdueCount, missingProcessEnd,
      alerts, monthlyTrend, workerChart
    };
  }, [rangedVisits, workers]);

  // ── Build manager views from Regions + Centers + Employees ─────────────
  useEffect(() => {
    const fetchManagers = async () => {
      try {
        setLoadingManagers(true);

        const [regionsSnap, centersSnap] = await Promise.all([
          getDocs(collection(db, 'Regions')),
          getDocs(collection(db, 'Centers'))
        ]);

        const allCenters = testFilters.centerDocs(centersSnap.docs).map(d => ({ id: d.id, ...d.data() }));
        const regions = regionsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }));

        const viewSeeds = regions.flatMap(region => {
          const seeds = [];

          if (region.managerId) {
            seeds.push({
              viewKey: region.id,
              scope: 'region',
              region,
              managerId: region.managerId,
              cities: region.cities || []
            });
          }

          Object.entries(region.cityManagerIds || {}).forEach(([cityKey, managerIds]) => {
            const cityName = (region.cities || []).find(city => normalizeCity(city) === cityKey) || cityKey;
            normalizeManagerIds(managerIds).forEach(managerId => {
              seeds.push({
                viewKey: `${region.id}:${cityKey}:${managerId}`,
                scope: 'city',
                region,
                managerId,
                cityName,
                cities: [cityName]
              });
            });
          });

          return seeds;
        });

        // Fetch manager employee docs in parallel (unique ids)
        const managerIds = [...new Set(viewSeeds.map(seed => seed.managerId).filter(Boolean))];
        const managerDocs = await Promise.all(
          managerIds.map(id => getDoc(doc(db, 'Employees', id)).catch(() => null))
        );
        const managerDataMap = {};
        managerIds.forEach((id, idx) => {
          const d = managerDocs[idx];
          if (d && d.exists()) managerDataMap[id] = d.data();
        });

        const views = viewSeeds.map(seed => {
          const { region } = seed;
          const managerData = managerDataMap[seed.managerId];
          if (!managerData) return null;

          const normalizedCities = (seed.cities || []).map(normalizeCity);
          const regionCenters = allCenters
            .filter(c => normalizedCities.includes(normalizeCity(c['İl'])))
            .map(c => ({
              id: c.id,
              name: c['Adı'] || '',
              city: c['İl'] || '',
              district: c['İlçe'] || '',
              status: c['Durum'] || 'Aktif'
            }));

          return {
            viewKey: seed.viewKey,
            regionId: region.id,
            regionName: seed.scope === 'city'
              ? `${region.name || ''} / ${seed.cityName}`
              : region.name || '',
            baseRegionName: region.name || '',
            regionColor: region.color || '#3b82f6',
            cities: seed.cities || [],
            managerId: seed.managerId,
            scope: seed.scope,
            cityName: seed.cityName || '',
            name: managerData.eName || '',
            surname: managerData.eSurname || '',
            email: managerData.eMail || '',
            phone: managerData.phone || '',
            centers: regionCenters,
            centerCount: regionCenters.length
          };
        }).filter(Boolean);

        views.sort((a, b) => a.regionName.localeCompare(b.regionName, 'tr'));
        setManagerViews(testFilters.people(views));
        setLoadingManagers(false);
      } catch (error) {
        console.error('Error fetching region managers:', error);
        toast.error(labelRef.current('toast.managersError'));
        setLoadingManagers(false);
      }
    };

    fetchManagers();
  }, [testFilters]);

  // Auto-select own region for non-admin bölge müdürü users
  useEffect(() => {
    if (isAdmin || selectedView || !employeeId) return;
    const ownViews = managerViews.filter(v => v.managerId === employeeId);
    if (ownViews.length === 1) {
      setSelectedView(ownViews[0]);
    }
  }, [managerViews, isAdmin, employeeId, selectedView]);

  // ── Fetch workers when a region is selected ─────────────────────────────
  const fetchWorkersForRegion = useCallback(async () => {
    if (!selectedView) {
      setWorkers([]);
      setProjectlessWorkers([]);
      return;
    }

    setLoadingWorkers(true);
    setExpandedWorkers({});

    try {
      const centerIds = selectedView.centers.map(c => c.id);

      const projectsSnapshot = await getDocs(collection(db, 'Projects'));
      testProjectIdsRef.current = getTestProjectIds(projectsSnapshot.docs);
      const projectDocs = testFilters.projectDocs(projectsSnapshot.docs);

      // empId -> { emp, projects: [] }
      const empProjectMap = {};

      for (const projectDoc of projectDocs) {
        const projectData = projectDoc.data();
        const projectEmployees = projectData.employees || [];

        const centerEmployees = projectEmployees.filter(emp =>
          centerIds.includes(emp.centerId)
        );

        for (const emp of centerEmployees) {
          const isPrimer = emp.workerType === 'primer' || emp.workerType === 'Primer';
          const isBlinded = emp.blindStatus === 'blinded' || emp.blindStatus === 'Blinded';
          const center = selectedView.centers.find(c => c.id === emp.centerId);

          const projectInfo = {
            projectId: projectDoc.id,
            // String(): sayısal kaydedilmiş proje kodları arama/sıralamada
            // metin işlemlerine girdiği için tipi burada sabitliyoruz.
            projectTitle: String(projectData.titleShort || projectData.titleLong || ''),
            role: emp.role || '',
            centerId: emp.centerId,
            centerName: emp.centerName || center?.name || '',
            centerCity: center?.city || '',
            assignedAt: emp.assignedAt,
            status: emp.status || 'active',
            // Merkezden ayrılmış atamalar rapordan düşmez, rozetle işaretlenir.
            isArchived: isArchivedAssignment(emp),
            unassignedAt: emp.unassignedAt || '',
            isPrimer,
            isBlinded,
            isFinished: projectData.isFinished === true
          };

          if (empProjectMap[emp.id]) {
            empProjectMap[emp.id].projects.push(projectInfo);
          } else {
            empProjectMap[emp.id] = { emp, projects: [projectInfo] };
          }
        }
      }

      const uniqueEmpIds = Object.keys(empProjectMap);
      const empDocs = await Promise.all(
        uniqueEmpIds.map(id => getDoc(doc(db, 'Employees', id)).catch(() => null))
      );
      const empDataMap = {};
      uniqueEmpIds.forEach((id, idx) => {
        const empDoc = empDocs[idx];
        empDataMap[id] = empDoc && empDoc.exists() ? empDoc.data() : null;
      });

      const workersData = uniqueEmpIds.map(id => {
        const { emp, projects } = empProjectMap[id];
        const employeeData = empDataMap[id];
        return {
          id,
          name: emp.name || '',
          surname: emp.surname || '',
          email: employeeData?.eMail || '',
          phone: employeeData?.phone || '',
          projects
        };
      });

      // Hiçbir projede aktif merkez ataması olmayan, Çalıştığı İl'i bu görünümün
      // illerinde olan aktif çalışanlar. Okunamazsa yalnızca bu liste boş kalır.
      let cityWorkers = [];
      try {
        const cities = new Set((selectedView.cities || []).map(foldCity));
        const cityEmployees = await fetchCityBasedEmployees(cities, getCenterAssignedIds(projectDocs));
        cityWorkers = cityEmployees
          .filter(emp => Number(emp.Active) !== 0)
          .map(emp => ({
            id: emp.id,
            name: emp.eName || '',
            surname: emp.eSurname || '',
            email: emp.eMail || '',
            phone: emp.phone || '',
            workCity: emp.workCity || ''
          }))
          .sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, 'tr'));
      } catch (cityError) {
        console.error('Error fetching project-less workers for region:', cityError);
      }

      workersData.sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`));
      setWorkers(testFilters.people(workersData));
      setProjectlessWorkers(testFilters.people(cityWorkers));
      setLoadingWorkers(false);
    } catch (error) {
      console.error('Error fetching workers for region:', error);
      toast.error(labelRef.current('toast.workersError'));
      setProjectlessWorkers([]);
      setLoadingWorkers(false);
    }
  }, [selectedView, testFilters]);

  useEffect(() => {
    fetchWorkersForRegion();
  }, [fetchWorkersForRegion]);

  // ── Fetch visits for region workers (batched 'in' queries) ─────────────
  useEffect(() => {
    if (workers.length === 0) { setAllVisits([]); return; }
    const fetchVisits = async () => {
      setLoadingVisits(true);
      try {
        const workerIds = workers.map(w => w.id);
        const BATCH_SIZE = 30;
        const batches = [];
        for (let i = 0; i < workerIds.length; i += BATCH_SIZE) {
          batches.push(workerIds.slice(i, i + BATCH_SIZE));
        }
        const results = await Promise.all(
          batches.map(batch => {
            const q = query(collection(db, 'Visits'), where('employeeId', 'in', batch));
            return getDocs(q);
          })
        );
        const visits = results.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setAllVisits(testFilters.records(visits, testProjectIdsRef.current));
      } catch (e) {
        console.error('Error fetching visits for region dashboard:', e);
      } finally {
        setLoadingVisits(false);
      }
    };
    fetchVisits();
  }, [workers, testFilters]);

  // The legacy `dark-mode` body class is managed by ThemeContext

  // ── Table scroll handlers ───────────────────────────────────────────────
  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [workers]);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      tableContainerRef.current.scrollTo({
        left: tableContainerRef.current.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount),
        behavior: 'smooth'
      });
    }
  };

  const handleViewClick = (view) => {
    if (selectedView?.viewKey === view.viewKey) {
      setSelectedView(null);
    } else {
      setSelectedView(view);
    }
  };

  const toggleWorkerExpand = (workerId) => {
    setExpandedWorkers(prev => ({ ...prev, [workerId]: !prev[workerId] }));
  };

  const handleViewVisits = useCallback((worker, project) => {
    const workerVisits = allVisits.filter(
      v => v.employeeId === worker.id && v.projectId === project.projectId
    );

    if (workerVisits.length === 0) {
      toast.info(labelRef.current('toast.noVisits'));
      return;
    }

    setSelectedEmployee({
      id: worker.id,
      name: `${worker.name} ${worker.surname}`,
      project: { id: project.projectId, title: project.projectTitle },
      visits: workerVisits
    });
    setShowVisitsModal(true);
  }, [allVisits]);

  // ── Filter workers ──────────────────────────────────────────────────────
  const filteredWorkers = useMemo(() => {
    return workers.map(worker => {
      let filteredProjects = worker.projects;

      if (statusFilter === 'active') {
        filteredProjects = filteredProjects.filter(p => p.status === 'active');
      } else if (statusFilter === 'inactive') {
        filteredProjects = filteredProjects.filter(p => p.status !== 'active');
      }

      if (primerFilter === 'primer') {
        filteredProjects = filteredProjects.filter(p => p.isPrimer === true);
      } else if (primerFilter === 'backup') {
        filteredProjects = filteredProjects.filter(p => p.isPrimer === false);
      }

      if (blindedFilter === 'blinded') {
        filteredProjects = filteredProjects.filter(p => p.isBlinded === true);
      } else if (blindedFilter === 'unblinded') {
        filteredProjects = filteredProjects.filter(p => p.isBlinded === false);
      }

      if (hideFinished) {
        filteredProjects = filteredProjects.filter(p => !p.isFinished);
      }

      if (searchTerm) {
        // Alanlar her zaman string değil (sayısal proje kodu, telefon vb.), bu
        // yüzden dönüşüm toSearchText üzerinden yapılır.
        const matchesWorker = matchesSearchTerm(
          searchTerm,
          `${worker.name} ${worker.surname}`,
          worker.email,
          worker.phone
        );

        if (!matchesWorker) {
          filteredProjects = filteredProjects.filter(p =>
            matchesSearchTerm(searchTerm, p.projectTitle, p.centerName, p.centerCity)
          );
        }
      }

      if (filteredProjects.length === 0) return null;
      return { ...worker, projects: filteredProjects };
    }).filter(Boolean);
  }, [workers, searchTerm, statusFilter, primerFilter, blindedFilter, hideFinished]);

  const filteredProjectlessWorkers = useMemo(() => {
    if (!searchTerm) return projectlessWorkers;
    return projectlessWorkers.filter(worker => matchesSearchTerm(
      searchTerm,
      `${worker.name} ${worker.surname}`,
      worker.email,
      worker.phone,
      worker.workCity
    ));
  }, [projectlessWorkers, searchTerm]);

  // ── Excel export ────────────────────────────────────────────────────────
  const downloadExcel = () => {
    const dataToExport = filteredWorkers.flatMap(worker =>
      worker.projects.map(project => ({
        'Bölge': selectedView?.regionName || '',
        'Bölge Müdürü': selectedView ? `${selectedView.name} ${selectedView.surname}` : '',
        'Çalışan Adı': `${worker.name} ${worker.surname}`,
        'E-posta': worker.email || '-',
        'Telefon': worker.phone || '-',
        'Merkez': project.centerName || '-',
        'Merkezden Ayrılma': project.isArchived ? formatUnassignedDate(project) : '',
        'İl': project.centerCity || '-',
        'Proje': project.projectTitle,
        'Rol': project.role,
        'Durum': project.status === 'active' ? 'Aktif' : 'Pasif',
        'Tip': project.isPrimer ? 'Primer' : 'Backup',
        'Kör Durumu': project.isBlinded ? 'Blinded' : 'Unblinded',
        'Atanma Tarihi': project.assignedAt?.toDate?.()?.toLocaleDateString('tr-TR') ||
                        (project.assignedAt instanceof Date ? project.assignedAt.toLocaleDateString('tr-TR') : '-')
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Çalışanlar');

    const regionName = selectedView ? selectedView.regionName.replace(/\s+/g, '_') : 'Tüm_Bölgeler';
    XLSX.writeFile(workbook, `${regionName}_Bölge_Çalışan_Listesi.xlsx`);
    toast.success(labelRef.current('toast.excelDownloaded'));
  };

  const downloadSummaryExcel = () => {
    if (!summary) return;
    const regionName = selectedView ? selectedView.regionName.replace(/\s+/g, '_') : 'Bölge';
    const data = [
      { 'Metrik': 'Toplam Vizit', 'Değer': summary.total },
      { 'Metrik': 'Ev Vizitleri', 'Değer': summary.homeCount },
      { 'Metrik': 'Hastane Vizitleri', 'Değer': summary.siteCount },
      { 'Metrik': 'Sorunsuz Vizit Oranı (%)', 'Değer': summary.qualityRate },
      { 'Metrik': 'SLA Karşılanma Oranı (%)', 'Değer': summary.slaRate },
      { 'Metrik': 'Dokümantasyon Tamlığı (%)', 'Değer': summary.docRate },
      { 'Metrik': 'Toplam KM', 'Değer': summary.totalKm },
      { 'Metrik': 'Sorunlu Vizit', 'Değer': summary.issueCount },
      { 'Metrik': 'Kaçırılan Vizit', 'Değer': summary.missedCount },
      { 'Metrik': 'Protokol Sapması', 'Değer': summary.deviationCount },
      { 'Metrik': 'AE/SAE', 'Değer': summary.aeSaeCount },
      { 'Metrik': 'Gecikmiş Vizit', 'Değer': summary.overdueCount },
      { 'Metrik': 'Süreç Bitiş Tarihi Eksik', 'Değer': summary.missingProcessEnd },
    ];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Operasyonel Özet');
    XLSX.writeFile(wb, `${regionName}_Operasyonel_Ozet.xlsx`);
    toast.success(labelRef.current('toast.summaryExcel'));
  };

  // Views visible to the current user
  const visibleViews = useMemo(() => {
    if (isAdmin) return managerViews;
    return managerViews.filter(v => v.managerId === employeeId);
  }, [managerViews, isAdmin, employeeId]);

  // Cities of the open region that have at least one centre, in the region's own order
  const cityGroups = selectedView
    ? selectedView.cities
      .map((city) => ({ city, centers: selectedView.centers.filter((c) => c.city === city) }))
      .filter((group) => group.centers.length > 0)
    : [];

  return (
    // `lang` keeps CSS upper-casing right in English (the document stays lang="tr")
    <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
      <PageHero
        icon={LuMap}
        gradient="tw-from-sky-400 tw-to-indigo-600 tw-shadow-sky-500/30"
        title={label('title')}
        subtitle={label('breadcrumb')}
      >
        {selectedView && workers.length > 0 && (
          <>
            <Button onClick={fetchWorkersForRegion} disabled={loadingWorkers} title={smo('header.refreshHint')}>
              <LuRefreshCw aria-hidden="true" className={`tw-h-4 tw-w-4 ${loadingWorkers ? 'tw-animate-spin' : ''}`} />
              {loadingWorkers ? smo('header.refreshing') : smo('header.refresh')}
            </Button>
            <Button variant="success" onClick={downloadExcel} title={smo('header.downloadHint')}>
              <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
              {smo('header.download')}
            </Button>
          </>
        )}
      </PageHero>

      {/* Stats Cards */}
      <div className={`tw-grid tw-gap-3 sm:tw-gap-4 ${selectedView ? 'sm:tw-grid-cols-3' : ''}`}>
        <Stat icon={LuMap} gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30" label={label('stats.totalRegions')} value={managerViews.length} />
        {selectedView && <Stat icon={LuBuilding} gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" label={label('stats.centers')} value={selectedView.centerCount} />}
        {selectedView && <Stat icon={LuUsers} gradient="tw-from-rose-400 tw-to-red-600 tw-shadow-rose-500/30" label={label('stats.workers')} value={workers.length} />}
      </div>

      {/* Region Manager Selection Grid */}
      <RegionPicker
        isAdmin={isAdmin}
        loadingManagers={loadingManagers}
        visibleViews={visibleViews}
        selectedView={selectedView}
        onSelect={handleViewClick}
      />

      {/* Workers Section */}
      {selectedView && (
        <section className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}>
          <div className="tw-flex tw-items-start tw-gap-3">
            <GlassIcon gradient="tw-from-teal-400 tw-to-emerald-600 tw-shadow-teal-500/30" size="xs" className="tw-mt-0.5">
              <LuUsers aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
            </GlassIcon>
            <div className="tw-min-w-0 tw-flex-1">
              <h2 className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-lg tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">
                <span aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0 tw-rounded-full tw-bg-[color:var(--region)]" style={{ '--region': selectedView.regionColor }} />
                {label('team.title', { region: selectedView.regionName, name: `${selectedView.name} ${selectedView.surname}` })}
              </h2>
              <p className="tw-mt-0.5 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('team.subtitle')}</p>
            </div>
          </div>

          {/* City groups with collapsible centers */}
          <CityCenterGroups
            groups={cityGroups}
            keyPrefix={selectedView.viewKey}
            collapsedCities={collapsedCities}
            setCollapsedCities={setCollapsedCities}
            accentColor={selectedView.regionColor}
          />

          {/* Search and Filter */}
          {workers.length > 0 && (
            <WorkerFilters
              workers={workers}
              showIncomplete={false}
              searchPlaceholder={label('filters.search')}
              help={label('filters.help')}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              primerFilter={primerFilter}
              setPrimerFilter={setPrimerFilter}
              blindedFilter={blindedFilter}
              setBlindedFilter={setBlindedFilter}
              hideFinished={hideFinished}
              setHideFinished={setHideFinished}
            />
          )}

          {loadingWorkers ? (
            <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-10 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
              <LuLoaderCircle aria-hidden="true" className="tw-h-5 tw-w-5 tw-animate-spin tw-text-teal-500" />
              {label('team.loading')}
            </p>
          ) : filteredWorkers.length > 0 ? (
            <WorkersTable
              filteredWorkers={filteredWorkers}
              expandedWorkers={expandedWorkers}
              toggleWorkerExpand={toggleWorkerExpand}
              missingDocsByWorker={null}
              incompleteVisitCounts={null}
              canRemoveSMO={false}
              tableContainerRef={tableContainerRef}
              handleScroll={handleScroll}
              showLeftScroll={showLeftScroll}
              showRightScroll={showRightScroll}
              scrollTable={scrollTable}
              detailsProps={{ showCity: true, onViewVisits: handleViewVisits }}
            />
          ) : filteredProjectlessWorkers.length === 0 && (
            <EmptyState icon={LuUsers} title={searchTerm ? label('team.noMatch') : label('team.empty')} />
          )}

          {!loadingWorkers && (
            <ProjectlessWorkers workers={filteredProjectlessWorkers} accentColor={selectedView.regionColor} />
          )}
        </section>
      )}

      {/* Operasyonel Genel Bakış */}
      {selectedView && (
        <ReportSection
          icon={LuChartColumn}
          gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30"
          title={label('summary.title')}
          description={label('summary.text')}
          status={loadingVisits
            ? <SectionStatus>{smo('section.loading')}</SectionStatus>
            : summary && <SectionStatus>{label('summary.status', { total: summary.total, home: summary.homeCount, site: summary.siteCount })}</SectionStatus>}
          onExcel={!loadingVisits && summary ? downloadSummaryExcel : undefined}
          expanded={summaryExpanded}
          onToggle={() => setSummaryExpanded(prev => !prev)}
          dateRange={(
            <DateRangeBar
              id="region-summary"
              start={dateStart}
              end={dateEnd}
              onStart={setDateStart}
              onEnd={setDateEnd}
              onThisMonth={() => { setDateStart(getMonthStart()); setDateEnd(getToday()); }}
              onAllTime={() => { setDateStart(''); setDateEnd(''); }}
            />
          )}
        >
          {!loadingVisits && !summary && <EmptyState compact icon={LuTriangleAlert} title={smo('section.noVisits')} />}
          {!loadingVisits && summary && summaryExpanded && <RegionSummary summary={summary} regionColor={selectedView.regionColor} />}
        </ReportSection>
      )}

      {/* Visits Modal */}
      {showVisitsModal && selectedEmployee && (
        <ProjectVisitsModal
          show={showVisitsModal}
          onHide={() => {
            setShowVisitsModal(false);
            setSelectedEmployee(null);
          }}
          project={selectedEmployee.project}
          employeeId={selectedEmployee.id}
          employeeName={selectedEmployee.name}
          visits={selectedEmployee.visits}
        />
      )}
    </div>
  );
};

export default BolgeMudurleriReports;
